import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import "react-loading-skeleton/dist/skeleton.css";
import styles from './index.module.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faRefresh } from "@fortawesome/free-solid-svg-icons";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { getAllCountries } from "../../../redux/features/countries/countriesService";
import { Link, useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import YearsDropdown from "../../../components/YearsDropdown/YearsDropdown";
import { getAllFeatures } from "../../../redux/features/features/featuresService";
import { getAllMakes } from "../../../redux/features/makes/makesService";
import { getModelByMakeId } from "../../../redux/features/models/modelsService";
import { getModelCodesByModelId } from "../../../redux/features/modelCodes/modelCodesService";
import { getAllColors } from "../../../redux/features/colors/colorsService";
import { getAllVehicleTypes } from "../../../redux/features/vehicleTypes/vehicleTypesService";
import { getAllFuels } from "../../../redux/features/fuels/fuelsService";
import { getAllTransmissions } from "../../../redux/features/transmissions/transmissionsService";
import { addNewVehicle } from "../../../redux/features/vehicleInvoice/vehicleInvoiceService";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 2MB Limit

const VehicleInvoice = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const countryDropdownRef = useRef(null);
    const steeringDropdownRef = useRef(null);
    const wheelDriveDropdownRef = useRef(null);
    const interiorDropdownRef = useRef(null);
    const exteriorDropdownRef = useRef(null);
    const featuresDropdownRef = useRef(null);
    const makesDropdownRef = useRef(null);
    const modelsDropdownRef = useRef(null);
    const modelCodesDropdownRef = useRef(null);
    const exColorDropdownRef = useRef(null);
    const inColorDropdownRef = useRef(null);
    const vehicleTypeDropdownRef = useRef(null);
    const fuelTypeDropdownRef = useRef(null);
    const transmissionDropdownRef = useRef(null);

    const { isVehicleCreating } = useSelector((state) => state.vehicle.newVehicle);
    const { isCountriesLoading, countries } = useSelector((state) => state.country.allCountries);
    const { isMakesLoading, makes } = useSelector((state) => state.make.allMakes);
    const { isModelsByMakeLoading, modelsByMake } = useSelector((state) => state.model.allModelsByMakeId);
    const { isVehicleTypesLoading, vehicleTypes } = useSelector((state) => state.vehicleType.allVehicleTypes);

    const [allVehicleFeatures, setAllVehicleFeatures] = useState([]);
    const [selectedSteering, setSelectedSteering] = useState(null);
    const [selectedWheelDrive, setSelectedWheelDrive] = useState(null);
    const [selectedExCondition, setSelectedExCondition] = useState(null);
    const [selectedInCondition, setSelectedInCondition] = useState(null);
    const [selectedMake, setSelectedMake] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);
    const [selectedVehicleType, setSelectedVehicleType] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        thumbnail: '',
        description: "",
        chassisNo: "",
        mileage: "",
        engineCapacity: "",
        regYear: "",
        mfgYear: "",
        makeId: "",
        modelId: "",
        countryId: "",
        vehicleTypeId: "",
    });

    const [errorMessages, setErrorMessages] = useState({
        name: '',
        file: '',
        description: "",
        chassisNo: "",
        mileage: "",
        engineCapacity: "",
        regYear: "",
        mfgYear: "",
        makeId: "",
        modelId: "",
        countryId: "",
        vehicleTypeId: "",
    });
    const [disabledBtn, setBtnDisable] = useState(false);

    //fetch data
    const fetchCountries = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllCountries(params));
        },
        [dispatch]
    );

    const fetchFeatures = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllFeatures(params));
        },
        [dispatch]
    );

    const fetchMakes = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllMakes(params));
        },
        [dispatch]
    );

    const fetchColors = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllColors(params));
        },
        [dispatch]
    );

    const fetchVehicleTypes = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllVehicleTypes(params));
        },
        [dispatch]
    );

    const fetchFuelTypes = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllFuels(params));
        },
        [dispatch]
    );

    const fetchTransmissions = useCallback(
        (filters = {}) => {
            const params = {
                status: true,
                ...filters,
            };
            dispatch(getAllTransmissions(params));
        },
        [dispatch]
    );

    const fetchModels = async (makeId) => {
        await dispatch(getModelByMakeId({ makeId: makeId })).unwrap()
    };

    const fetchModelCodes = async (modelId) => {
        await dispatch(getModelCodesByModelId({ modelId: modelId })).unwrap()
    };

    useEffect(() => {
        fetchCountries();
        fetchFeatures();
        fetchMakes();
        fetchColors();
        fetchVehicleTypes();
        fetchFuelTypes();
        fetchTransmissions();
    }, [fetchCountries, fetchFeatures, fetchMakes, fetchColors, fetchVehicleTypes, fetchFuelTypes, fetchTransmissions]);

    const handleSelect = (option) => {
        setFormData({ ...formData, countryId: option.id });
        setErrorMessages({ ...errorMessages, countryId: "" });
        setSelectedCountry({
            id: option.id,
            name: option.name
        });
    };

    const handleMakeSelect = (option) => {
        setFormData({ ...formData, makeId: option.id });
        setErrorMessages({ ...errorMessages, makeId: "" });
        setSelectedModel(null);
        fetchModels(option.id);
        setSelectedMake({
            id: option.id,
            name: option.name
        });
    };

    const handleModelSelect = (option) => {
        setFormData({ ...formData, modelId: option.id });
        setErrorMessages({ ...errorMessages, modelId: "" });
        fetchModelCodes(option.id);
        setSelectedModel({
            id: option.id,
            name: option.name
        });
    };

    const handleVehicleTypeSelect = (option) => {
        setFormData({ ...formData, vehicleTypeId: option.id });
        setErrorMessages({ ...errorMessages, vehicleTypeId: "" });
        setSelectedVehicleType({
            id: option.id,
            name: option.name
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleRegYearChange = (selectedMonth) => {
        setFormData((prev) => ({ ...prev, regYear: selectedMonth.name }));
        setErrorMessages({ ...errorMessages, regYear: '' });
    };

    const handleMfgYearChange = (selectedMonth) => {
        setFormData((prev) => ({ ...prev, mfgYear: selectedMonth.name }));
        setErrorMessages({ ...errorMessages, mfgYear: '' });
    };

    const validateFile = (thumbnail) => {
        if (thumbnail) {
            if (!thumbnail.type.startsWith("image/")) {
                setErrorMessages({ ...errorMessages, file: "Only image files are allowed." });
                return;
            }

            setErrorMessages({ ...errorMessages, file: "" });
            setFormData({ ...formData, thumbnail });
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateFile(selectedFile);
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateFile(selectedFile);
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    // Validate input
    const isValidVehicleFirst = () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = "Name is required.";
        }
        if (!formData.chassisNo) {
            errors.chassisNo = "Chassis Number is required.";
        }
        if (!formData.mileage) {
            errors.mileage = "Mileage is required.";
        }
        if (!formData.engineCapacity) {
            errors.engineCapacity = "Engine Capacity is required.";
        }
        if (!formData.regYear) {
            errors.regYear = "Registration Year is required.";
        }
        if (!formData.mfgYear) {
            errors.mfgYear = "Manufacturing Year is required.";
        }
        if (!selectedCountry) {
            errors.country = "Country is required.";
        }if (!formData.makeId) errors.makeId = "Vehicle make is required.";
        if (!formData.modelId) errors.modelId = "Model is required.";
        if (!formData.vehicleTypeId) errors.vehicleTypeId = "Vehicle type is required.";

        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    // Validate input
    const isValidVehicle = () => {
        const errors = {};
        const trimmedName = formData.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!formData.thumbnail) {
            errors.thumbnail = "Image is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const resetAllSelections = () => {
        setSelectedWheelDrive(null);
        setSelectedExCondition(null);
        setSelectedInCondition(null);
        setSelectedMake(null);
        setSelectedModel(null);
        setSelectedCountry(null);
    };

    const insertNewVehicle = async () => {
        // if (isValidVehicle(formData)) {
        setBtnDisable(true);
        try {
            const response = await dispatch(addNewVehicle(formData)).unwrap();
            setBtnDisable(false);
            setFormData({
                name: '',
                file: '',
                images: [null]
            });
            setAllVehicleFeatures([]);
            if (countryDropdownRef.current) countryDropdownRef.current.clearSelection();
            if (steeringDropdownRef.current) steeringDropdownRef.current.clearSelection();
            if (wheelDriveDropdownRef.current) wheelDriveDropdownRef.current.clearSelection();
            if (interiorDropdownRef.current) interiorDropdownRef.current.clearSelection();
            if (exteriorDropdownRef.current) exteriorDropdownRef.current.clearSelection();
            if (featuresDropdownRef.current) featuresDropdownRef.current.clearSelection();
            if (makesDropdownRef.current) makesDropdownRef.current.clearSelection();
            if (modelsDropdownRef.current) modelsDropdownRef.current.clearSelection();
            if (modelCodesDropdownRef.current) modelCodesDropdownRef.current.clearSelection();
            if (exColorDropdownRef.current) exColorDropdownRef.current.clearSelection();
            if (inColorDropdownRef.current) inColorDropdownRef.current.clearSelection();
            if (vehicleTypeDropdownRef.current) vehicleTypeDropdownRef.current.clearSelection();
            if (fuelTypeDropdownRef.current) fuelTypeDropdownRef.current.clearSelection();
            if (transmissionDropdownRef.current) transmissionDropdownRef.current.clearSelection();
            resetAllSelections();
            navigate('/admin/vehicles')
            showToast('success', response.message);
        }
        catch (error) {
            setBtnDisable(false);
            showToast('error', error.message);
        };
        // }
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Vehicle</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={styles.vehicleFormGroup}>
                            <div className={`form-input-row`}>
                                <div className={`form-input-col md-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Name<span className={`required-star`}>*</span>
                                        </label>
                                        <input
                                            type='text'
                                            id='name'
                                            name='name'
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder='Enter Vehicle Name'
                                            className={`form-input-default`}
                                        />
                                        {errorMessages.name && (
                                            <p className={`text-danger`}>{errorMessages.name}</p>
                                        )}
                                    </div>
                                </div>
                                <div className={`form-input-col md-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Chassis Number
                                        </label>
                                        <input
                                            type='text'
                                            id='chassisNo'
                                            name='chassisNo'
                                            value={formData.chassisNo}
                                            onChange={handleInputChange}
                                            placeholder='Enter Chassis Number'
                                            className={`form-input-default`}
                                        />
                                        {errorMessages.chassisNo && (
                                            <p className={`text-danger`}>{errorMessages.chassisNo}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={`form-input-row md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Description
                                    </label>
                                    <input
                                        type='textarea'
                                        id='description'
                                        name='description'
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder='Enter the description'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.description && (
                                        <p className={`text-danger`}>{errorMessages.description}</p>
                                    )}
                                </div>
                            </div>
                            <div className={`form-input-row`}>
                                <div className={`form-input-col md-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Mileage (km)
                                        </label>
                                        <input
                                            type='number'
                                            id='mileage'
                                            name='mileage'
                                            value={formData.mileage}
                                            onChange={handleInputChange}
                                            placeholder='Enter Mileage'
                                            className={`form-input-default`}
                                        />
                                        {errorMessages.mileage && (
                                            <p className={`text-danger`}>{errorMessages.mileage}</p>
                                        )}
                                    </div>
                                </div>
                                <div className={`form-input-col md-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Engine Capacity (cc)
                                        </label>
                                        <input
                                            type='number'
                                            id='engineCapacity'
                                            name='engineCapacity'
                                            value={formData.engineCapacity}
                                            onChange={handleInputChange}
                                            placeholder='Enter Engine Capacity'
                                            className={`form-input-default`}
                                        />
                                        {errorMessages.engineCapacity && (
                                            <p className={`text-danger`}>{errorMessages.engineCapacity}</p>
                                        )}
                                    </div>
                                </div>
                                {/* <div className={`form-input-col sm-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Stock No
                                            </label>
                                            <input
                                                type='text'
                                                id='stockNo'
                                                name='stockNo'
                                                value={formData.stockNo}
                                                onChange={handleInputChange}
                                                placeholder='Enter Stock Number'
                                                className={`form-input-default`}
                                            />
                                            {errorMessages.stockNo && (
                                                <p className={`text-danger`}>{errorMessages.stockNo}</p>
                                            )}
                                        </div>
                                    </div> */}
                            </div>
                            <div className={`form-input-row`}>
                                <div className={`form-input-col sm-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Reg. Year
                                        </label>
                                        <YearsDropdown onChange={handleRegYearChange} value={formData.regYear} />
                                        {errorMessages.regYear && (
                                            <p className={`text-danger`}>{errorMessages.regYear}</p>
                                        )}
                                    </div>
                                </div>
                                <div className={`form-input-col sm-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Mfg. Year
                                        </label>
                                        <YearsDropdown onChange={handleMfgYearChange} value={formData.mfgYear} />
                                        {errorMessages.mfgYear && (
                                            <p className={`text-danger`}>{errorMessages.mfgYear}</p>
                                        )}
                                    </div>
                                </div>
                                <div className={`form-input-col md-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Country
                                            <Link to="/admin/countries" target="_blank" style={{ cursor: "pointer", color: "black", marginLeft: "5px", marginRight: "5px" }}><FontAwesomeIcon icon={faPlusCircle} /></Link>
                                            {isCountriesLoading ?
                                                <ClipLoader size={15} /> : <FontAwesomeIcon icon={faRefresh} onClick={fetchCountries} style={{ cursor: "pointer" }} />
                                            }
                                        </label>
                                        <Dropdown
                                            ref={countryDropdownRef}
                                            options={countries}
                                            placeholder="Choose an Option"
                                            onSelect={handleSelect}
                                            value={selectedCountry?.name}
                                        />
                                        {errorMessages.stockNo && (
                                            <p className={`text-danger`}>{errorMessages.stockNo}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={`form-input-row`}>
                                <div className={`form-input-col md-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Vehicle Type
                                            <Link to="/admin/vehicle-types" target="_blank" style={{ cursor: "pointer", color: "black", marginLeft: "5px", marginRight: "5px" }}><FontAwesomeIcon icon={faPlusCircle} /></Link>
                                            {isVehicleTypesLoading ?
                                                <ClipLoader size={15} /> : <FontAwesomeIcon icon={faRefresh} onClick={fetchVehicleTypes} style={{ cursor: "pointer" }} />
                                            }
                                        </label>
                                        <Dropdown
                                            ref={vehicleTypeDropdownRef}
                                            options={vehicleTypes}
                                            placeholder="Choose an Option"
                                            onSelect={handleVehicleTypeSelect}
                                            value={selectedVehicleType?.name}
                                        />
                                        {errorMessages.vehicleTypeId && (
                                            <p className={`text-danger`}>{errorMessages.vehicleTypeId}</p>
                                        )}
                                    </div>
                                </div>
                                <div className={`form-input-col md-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Make
                                            <Link to="/admin/makes" target="_blank" style={{ cursor: "pointer", color: "black", marginLeft: "5px", marginRight: "5px" }}><FontAwesomeIcon icon={faPlusCircle} /></Link>
                                            {isMakesLoading ?
                                                <ClipLoader size={15} /> : <FontAwesomeIcon icon={faRefresh} onClick={fetchMakes} style={{ cursor: "pointer" }} />
                                            }
                                        </label>
                                        <Dropdown
                                            ref={makesDropdownRef}
                                            options={makes}
                                            placeholder="Choose an Option"
                                            onSelect={handleMakeSelect}
                                            value={selectedMake?.name}
                                        />
                                        {errorMessages.makeId && (
                                            <p className={`text-danger`}>{errorMessages.makeId}</p>
                                        )}
                                    </div>
                                </div>
                                <div className={`form-input-col sm-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Model
                                            <Link to="/admin/models" target="_blank" style={{ cursor: "pointer", color: "black", marginLeft: "5px", marginRight: "5px" }}><FontAwesomeIcon icon={faPlusCircle} /></Link>
                                            {selectedMake && selectedMake.id !== null && isModelsByMakeLoading ? (
                                                <ClipLoader size={15} />
                                            ) : (
                                                <FontAwesomeIcon
                                                    icon={faRefresh}
                                                    onClick={() => selectedMake && fetchModels(selectedMake.id)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}
                                        </label>
                                        <Dropdown
                                            ref={modelsDropdownRef}
                                            options={modelsByMake}
                                            placeholder="Choose an Option"
                                            onSelect={handleModelSelect}
                                            value={selectedModel?.name}
                                        />
                                        {errorMessages.modelId && (
                                            <p className={`text-danger`}>{errorMessages.modelId}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.vehicleFormGroup}>
                            <div className={`form-input-row`}>
                                <div className={`form-input-col`}>
                                    <div className={`form-input-group`}>
                                        <label htmlFor='full_name' className={`form-label`}>
                                            Thumbnail Image<span className={`required-star`}>*</span>
                                        </label>
                                        <div
                                            onDrop={handleDrop}
                                            onDragOver={(event) => event.preventDefault()}
                                            onClick={handleClick}
                                            className={`drop-input`}
                                        >
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                style={{ display: "none" }}
                                            />
                                            {(!formData.thumbnail) ? <p>Drag & Drop a file here or click to select</p>
                                                :
                                                <div className={`inserted-file`}>
                                                    <div className={`file-card`}>
                                                        <img src={URL.createObjectURL(formData.thumbnail)} alt='File Type' className={`${styles.thumbnailImg} left-icon`} />
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                        {errorMessages.file && (
                                            <p className={`text-danger`}>{errorMessages.file}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewVehicle}
                        >
                            {isVehicleCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default VehicleInvoice;
